import React from 'react';
import PropTypes from 'prop-types';
import {
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Container,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import dar_trash from '../../../Assets/images/svg/dar_delete.svg';
import dar_pencil from '../../../Assets/images/svg/dar_pencil.svg';
import { removeDefaultStyles } from '../../../Utils/buttonStyle';
import styles from './bufferCalculation.module.scss';

const BufferCalculationOption = ({
  item,
  option,
  handleFieldChange,
  handleIncrement,
  handleDecrement,
  saveEffort,
  deleteEffort,
  editEfforts,
  timeUnits,
  hasAccess,
}) => (
  <div key={option.id} className={styles['bufferCalculation-option']}>
    {option.edit ? (
      <div>
        <div className={styles['bufferCalculation-row']}>
          <Container sx={{ padding: '0px !important', textAlign: 'start' }}>
            <TextField
              variant="outlined"
              placeholder="Enter text"
              sx={{
                padding: 0,
                width: '180px',
                '& .MuiInputBase-root': { height: '32px' },
              }}
              value={option.name || ''}
              onChange={(event) =>
                handleFieldChange(item.id, option.id, 'name', event.target.value)
              }
            />
          </Container>
          <div>
            <button
              style={{
                ...removeDefaultStyles,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: hasAccess('update') ? 'pointer' : 'not-allowed',
                opacity: hasAccess('update') ? 1 : 0.5,
              }}
              onClick={() => deleteEffort(item.id, option.id)}
              disabled={!hasAccess('delete')}
            >
              <img
                src={dar_trash}
                alt="Trash"
                className={styles['bufferCalculation-delete']}
              />
            </button>
          </div>
        </div>
        <div
          className={styles['bufferCalculation-row']}
          style={{ marginTop: 12 }}
        >
          <div className={styles['bufferCalculation-timeInputWrapper']}>
            <OutlinedInput
              value={option.value}
              onChange={(event) =>
                handleFieldChange(item.id, option.id, 'value', event.target.value)
              }
              type="number"
              startAdornment={
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => handleDecrement(item.id, option.id)}
                    size="small"
                    sx={{ padding: 0 }}
                  >
                    <RemoveIcon fontSize="small" sx={{ fontSize: '14px', color: '#6B5CE8' }} />
                  </IconButton>
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleIncrement(item.id, option.id)}
                    size="small"
                    sx={{ padding: 0 }}
                  >
                    <AddIcon fontSize="small" sx={{ fontSize: '14px', color: '#6B5CE8' }} />
                  </IconButton>
                </InputAdornment>
              }
              inputProps={{ style: { textAlign: 'center' } }}
              sx={{
                width: '90px',
                height: '32px',
                fontSize: '14px',
                backgroundColor: '#ECEDF4',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ccc',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    display: 'none',
                  },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
              }}
            />
            <div className={styles['bufferCalculation-selectWrapper']}>
              <Container sx={{ padding: '0px !important' }}>
                <Select
                  value={option.unit_type}
                  onChange={(event) =>
                    handleFieldChange(
                      item.id,
                      option.id,
                      'unit_type',
                      event.target.value,
                    )
                  }
                  sx={{
                    width: '80px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#ECEDF4',
                    '& .MuiSelect-select': {
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0px',
                      textAlign: 'center',
                    },
                  }}
                >
                  {timeUnits.map((unit, idx) => (
                    <MenuItem key={idx + 1} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </Container>
            </div>
          </div>
          <button
            style={{
              ...removeDefaultStyles,
              opacity: !option.name.trim() ? 0.5 : 1,
              cursor: !option.name.trim() ? 'not-allowed' : 'pointer',
            }}
            onClick={() => saveEffort(item.id, option.id)}
            disabled={!option.name.trim()}
          >
            <div className={styles['bufferCalculation-save']}>Save</div>
          </button>
        </div>
      </div>
    ) : (
      <div>
        <div className={styles['bufferCalculation-row']}>
          <div className={styles['bufferCalculation-priority']}>
            {option.name}
          </div>
          <button
            style={{
              ...removeDefaultStyles,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: hasAccess('update') ? 'pointer' : 'not-allowed',
              opacity: hasAccess('update') ? 1 : 0.5,
            }}
            onClick={() => editEfforts(item.id, option.id)}
            disabled={!hasAccess('update')}
          >
            <img
              src={dar_pencil}
              alt="Edit"
              className={styles['bufferCalculation-edit']}
            />
          </button>
        </div>
        <div className={styles['bufferCalculation-summary']}>
          {option.value}{' '}
          <span className={styles['bufferCalculation-parameter']}>
            {option.unit_type}{option.value > 1 && 's'} Buffer
          </span>
        </div>
      </div>
    )}
  </div>
);

BufferCalculationOption.propTypes = {
  item: PropTypes.object.isRequired,
  option: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleIncrement: PropTypes.func.isRequired,
  handleDecrement: PropTypes.func.isRequired,
  saveEffort: PropTypes.func.isRequired,
  deleteEffort: PropTypes.func.isRequired,
  editEfforts: PropTypes.func.isRequired,
  timeUnits: PropTypes.array.isRequired,
  hasAccess: PropTypes.func.isRequired,
};

export default BufferCalculationOption;
